<script lang="ts">
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import { orderStore } from "/@/stores/OrderStore"
  import { searchStore } from "/@/stores/SearchStore"
  import { selectedOrder } from "/@/stores/SelectedOrderStore"
  import Header from "/@lib/components/Header.svelte"
  import BalanceView from "/@lib/components/views/BalanceView.svelte"
  import CustomerDetailView from "/@lib/components/views/CustomerDetailView.svelte"
  import CustomerSearchView from "/@lib/components/views/CustomerSearchView.svelte"
  import CustomerTableView from "/@lib/components/views/CustomerTableView.svelte"
  import NotesView from "/@lib/components/views/NotesView.svelte"
  import OrdersView from "/@lib/components/views/OrdersView.svelte"
  import PositionsView from "/@lib/components/views/PositionsView.svelte"
  import { session, twApiClient } from "/@lib/tastyworks-rest"
  import { MoreMinimal } from "@tastyworks/icons-2.0"
  import {
    CustomerAccountRowResult,
    ItemsResponse,
    type AdminCustomerAccountParams,
  } from "@tastyworks/tastyworks-api"
  import { Tabs } from "@tastyworks/ui-library"
  import { Pane, PaneGroup, PaneResizer } from "paneforge"
  import { onMount } from "svelte"
  import { Temporal } from "temporal-polyfill"
  import LoadingSpinner from "./lib/components/loading/LoadingSpinner.svelte"

  window.Temporal = Temporal

  type SearchMode = "default" | "account" | "customer"
  type SearchStoreState = {
    accountNumber: string
    firstName: string
    lastName: string
    login: string
    phoneNumber: string
    taxNumber: string
  }

  let customerAccountPromise: Promise<ItemsResponse<CustomerAccountRowResult> | null> =
    $state(Promise.resolve(null))
  let searchMode: SearchMode = $state("default")
  let searchModeStatus: string | null = $state(null)
  // Single-level snapshot — only captured when entering a filtered mode from
  // "default", so transitioning account ↔ customer keeps the same anchor and
  // dismiss always returns to the user's most recent regular search.
  let priorCustomerAccountPromise: Promise<ItemsResponse<CustomerAccountRowResult> | null> | null =
    null
  let priorSearchStore: SearchStoreState | null = null

  function snapshotPriorIfEnteringFromDefault() {
    if (searchMode === "default") {
      priorCustomerAccountPromise = customerAccountPromise
      // structuredClone so subsequent edits in filtered modes don't mutate
      // the snapshot we'd restore on dismiss.
      priorSearchStore = structuredClone($searchStore)
    }
  }

  function searchCustomers(params: AdminCustomerAccountParams) {
    searchMode = "default"
    searchModeStatus = null
    priorCustomerAccountPromise = null
    priorSearchStore = null
    customerAccountPromise =
      twApiClient.AdminCustomerAccountService.index(params)
  }

  function resetCustomerResults(): void {
    searchMode = "default"
    searchModeStatus = null
    priorCustomerAccountPromise = null
    priorSearchStore = null
    customerAccountPromise = Promise.resolve(null)
    orderStore.reset()
    selectedCustomerAccount.reset()
    selectedOrder.reset()
  }

  // Note: unlike the original onResetCustomers + onSearchCustomers flow, the
  // mode handlers below intentionally preserve $selectedCustomerAccount so
  // the right pane stays consistent with the row the user just right-clicked.
  // Dismiss reverts both the results promise and the search-form state.

  function searchOnlyAccount(accountNumber: string) {
    snapshotPriorIfEnteringFromDefault()
    searchMode = "account"
    searchModeStatus = `Viewing all instances of account ${accountNumber}`
    $searchStore = {
      accountNumber,
      firstName: "",
      lastName: "",
      login: "",
      phoneNumber: "",
      taxNumber: "",
    }
    customerAccountPromise = twApiClient.AdminCustomerAccountService.index({
      accountNumber,
      partialNameSearch: true,
    })
  }

  function searchOnlyCustomer(row: CustomerAccountRowResult) {
    const customerId = row.customer?.id
    if (!customerId) {
      return
    }
    const username = row.user?.username ?? ""
    const email = row.user?.email ?? row.customer?.email ?? ""
    snapshotPriorIfEnteringFromDefault()
    searchMode = "customer"
    searchModeStatus = formatCustomerStatus(username, email)
    // The customer endpoint doesn't map to any search-form field, so clear
    // $searchStore to avoid leaving stale form state visible from the
    // previous regular search.
    searchStore.reset()
    // /customers/{id}/accounts only returns {account, authority-level}, so
    // synthesizing rows from that response alone leaves `tradingStatus`
    // empty (breaking the "Trading Level" / "Account Type" cells in the
    // detail panel, and disabling any context-menu actions gated on trading
    // status).
    //
    // The bundled /customers-accounts index endpoint, on the other hand,
    // returns trading-statuses + full authority objects but doesn't expose
    // a "by customer id" filter. So we use both: fetch the account list
    // by customer id, then fan out parallel index lookups per account
    // number and filter each response down to the target customer's row.
    // Result: one row per account, hydrated with real trading status and
    // the customer's actual authority on that account.
    const fetchPromise: Promise<ItemsResponse<CustomerAccountRowResult>> =
      twApiClient.AdminCustomerAccountService.fetchAccountsByCustomerId(
        customerId
      )
        .then(async (response) => {
          const accounts = response.items ?? []
          // Promise.allSettled instead of all so a single failed lookup
          // doesn't yank the user out of customer mode — log the failures
          // and surface every account we did hydrate successfully.
          const hydrated = await Promise.allSettled(
            accounts.map((account) =>
              twApiClient.AdminCustomerAccountService.index({
                accountNumber: account.accountNumber,
                partialNameSearch: true,
              })
            )
          )
          const items: CustomerAccountRowResult[] = []
          hydrated.forEach((settled, i) => {
            if (settled.status === "rejected") {
              console.error(
                "Failed to hydrate customer-mode row for account",
                accounts[i].accountNumber,
                settled.reason
              )
              return
            }
            // The bundled response can include multiple customers per
            // account (joint accounts, business accounts with multiple
            // authorities). Filter to the target customer so the table
            // still reads as "this customer's accounts" rather than
            // "this customer + every joint owner".
            const customerRow = (settled.value.items ?? []).find(
              (item) => item.customer?.id === customerId
            )
            if (customerRow) {
              items.push(customerRow)
            }
          })
          const wrapped = new ItemsResponse<CustomerAccountRowResult>()
          wrapped.items = items
          return wrapped
        })
        .catch((error) => {
          console.error(
            "Failed to fetch accounts by customer ID",
            customerId,
            error
          )
          // Only dismiss if this request is still the active one. If the
          // user already moved on (dismissed, switched mode, ran another
          // search), customerAccountPromise points elsewhere and we
          // shouldn't yank them out of that state.
          if (customerAccountPromise === fetchPromise) {
            dismissSearchMode()
          }
          return new ItemsResponse<CustomerAccountRowResult>()
        })
    customerAccountPromise = fetchPromise
  }

  function formatCustomerStatus(username: string, email: string): string {
    if (username && email) {
      return `Viewing all accounts for ${username} (${email})`
    }
    const identifier = username || email
    return identifier
      ? `Viewing all accounts for ${identifier}`
      : "Viewing all accounts for this customer"
  }

  function dismissSearchMode(): void {
    searchMode = "default"
    searchModeStatus = null
    if (priorCustomerAccountPromise) {
      customerAccountPromise = priorCustomerAccountPromise
      priorCustomerAccountPromise = null
    }
    if (priorSearchStore) {
      $searchStore = priorSearchStore
      priorSearchStore = null
    }
  }

  function preventDefault(event: Event): void {
    // Suppress the native context menu globally so VanillaContextMenu's
    // right-click handlers can render the admin actions menu — except on
    // text-input surfaces, where the user still needs the native menu for
    // copy/paste.
    const target = event.target
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      return
    }
    event.preventDefault()
  }

  async function validateSession() {
    try {
      const response = await twApiClient.AdminSessionService.validate(
        session.sessionToken
      )

      if (!response.hasData()) {
        session.clear()
        window.location.replace("/login.html")
      } else {
        return response.data
      }
    } catch (error) {
      console.error("Error validating session", error)
      window.location.replace("/login.html")
    }
  }

  const paneConfig = {
    leftPane: {
      defaultSize: 50,
      collapsible: false,
      minSize: 35,
    },
    rightPane: {
      defaultSize: 50,
      collapsible: false,
      minSize: 35,
    },
    customerTable: {
      defaultSize: 45,
      collapsible: false,
      minSize: 20,
    },
    customerDetail: {
      defaultSize: 55,
      collapsible: false,
      minSize: 30,
    },
  }

  onMount(() => {
    const keepAliveInterval = setInterval(
      () => {
        validateSession()
      },
      5 * 60 * 1000
    )

    return () => {
      clearInterval(keepAliveInterval)
    }
  })
</script>

<svelte:head>
  <title
    >{!session.currentUsername ? "" : `${session.currentUsername} - `}Trade Desk{__DEPLOY_LABEL__
      ? ` ${__DEPLOY_LABEL__}`
      : ""}{__GIT_HASH__ ? ` version ${__GIT_HASH__}` : ""}</title
  >
</svelte:head>

{#await validateSession()}
  <LoadingSpinner />
{:then data}
  {#if data !== undefined}
    <div
      class="flex h-screen w-full flex-col flex-nowrap overflow-hidden bg-trade-desk-gray-light"
      role="main"
      oncontextmenu={preventDefault}
    >
      <PaneGroup direction="horizontal" autoSaveId="tradedesk-layout">
        <Pane
          defaultSize={paneConfig.leftPane.defaultSize}
          collapsible={paneConfig.leftPane.collapsible}
          minSize={paneConfig.leftPane.minSize}
        >
          <div class="flex h-full w-full flex-col gap-2 p-1.5 pr-0">
            <CustomerSearchView
              onSearchCustomers={searchCustomers}
              onResetCustomers={resetCustomerResults}
            />
            <PaneGroup direction="vertical" autoSaveId="customer-table-group">
              <Pane
                defaultSize={paneConfig.customerTable.defaultSize}
                collapsible={paneConfig.customerTable.collapsible}
                minSize={paneConfig.customerTable.minSize}
              >
                <CustomerTableView
                  {customerAccountPromise}
                  {searchMode}
                  {searchModeStatus}
                  onSearchOnlyAccount={searchOnlyAccount}
                  onSearchOnlyCustomer={searchOnlyCustomer}
                  onDismissSearchMode={dismissSearchMode}
                />
              </Pane>
              <PaneResizer
                class="my-1 flex h-2 min-h-1 w-full items-center justify-center rounded-md bg-trade-desk-gray hover:bg-trade-desk-gray-dark"
              >
                <MoreMinimal />
              </PaneResizer>
              <Pane
                defaultSize={paneConfig.customerDetail.defaultSize}
                minSize={paneConfig.customerDetail.minSize}
                collapsible={paneConfig.customerDetail.collapsible}
                preserveWidth
              >
                <div class="h-full overflow-auto">
                  <CustomerDetailView class="w-full" />
                </div>
              </Pane>
            </PaneGroup>
          </div>
        </Pane>

        <PaneResizer
          class="mx-1 flex h-full w-2 min-w-2 flex-col items-center justify-center rounded-md bg-trade-desk-gray hover:bg-trade-desk-gray-dark"
        >
          <MoreMinimal class="rotate-90" />
        </PaneResizer>

        <Pane
          defaultSize={paneConfig.rightPane.defaultSize}
          collapsible={paneConfig.rightPane.collapsible}
          minSize={paneConfig.rightPane.minSize}
        >
          <Tabs.Root
            value="Notes"
            orientation="horizontal"
            dir="ltr"
            class="relative flex h-full max-w-[100%] basis-1/2 flex-col border-l-4 p-2"
          >
            <Tabs.List class="bg-blu flex w-full justify-between self-start">
              <div>
                {#each ["Notes", "Orders", "Positions", "Balances"] as tab}
                  <Tabs.Trigger class="data-[state=active]:bg-white" value={tab}
                    >{tab}</Tabs.Trigger
                  >
                {/each}
              </div>

              <Header />
            </Tabs.List>

            <Tabs.Content value="Notes" class="flex flex-grow flex-col">
              <NotesView />
            </Tabs.Content>

            <Tabs.Content value="Orders" class="flex flex-grow flex-col">
              <OrdersView />
            </Tabs.Content>

            <Tabs.Content value="Positions" class="flex flex-grow flex-col">
              <PositionsView />
            </Tabs.Content>

            <Tabs.Content value="Balances" class="flex flex-grow flex-col">
              <BalanceView />
            </Tabs.Content>
          </Tabs.Root>
        </Pane>
      </PaneGroup>
    </div>
  {/if}
{/await}
